/**
 * The Knowledge Overview's section list.
 *
 * The backend's context registry is structural only — it has no human labels
 * (see backend/services/contextEngine/registry.js). This file is where each
 * knowledge entity gets its plain-English name, its icon, its tile color from
 * the approved palette, and the page that edits it (when one exists).
 *
 * Order here is the order shown on the Overview page, and it MATCHES the
 * sidebar menu (config/knowledgeNav.ts) group for group — one order
 * everywhere (Jens 2026-08-18): Foundation → Audience → Offer →
 * Voice & rules → Content. The further down, the more derived the content.
 *
 * Entities deliberately NOT listed: internal/derived ones the merchant never
 * edits or thinks about (business_source, first_paint, performance_snapshot,
 * extra_context, ads_universe, metrics, seasonal_calendar, website_blueprint,
 * content_arc, cta_library, keyword_universe). They still count toward the
 * backend's completeness figure; they just don't earn a row.
 */
import type { Component } from 'vue'
import i18n from '@/i18n'
import {
  PhBriefcase,
  PhUsersThree,
  PhUsers,
  PhStorefront,
  PhFlag,
  PhTarget,
  PhFunnel,
  PhProhibit,
  PhChatCircle,
  PhChatCircleText,
  PhPalette,
  PhSquaresFour,
  PhListChecks,
  PhPackage,
  PhLightning,
  PhShieldWarning,
  PhMagnet,
} from '@phosphor-icons/vue'
import { AREA_TILES, type AreaTile } from './areaColors'

export interface KnowledgeSectionDef {
  /** Context-engine entity type — the key the backend reports status for. */
  type: string
  label: string
  /** One line explaining what this holds, in the merchant's language. */
  description: string
  icon: Component
  tile: AreaTile
  /** Knowledge page slug that edits it; omitted when the only way to fill it
   *  is by answering a question. */
  slug?: string
}

export const KNOWLEDGE_SECTIONS: KnowledgeSectionDef[] = [
  // ── Foundation ──────────────────────────────────────────────────────
  {
    type: 'business_truth.business_brief',
    get label() { return i18n.global.t('knowledgeSections.yourBusinessLabel') },
    get description() { return i18n.global.t('knowledgeSections.yourBusinessDescription') },
    icon: PhBriefcase,
    tile: AREA_TILES.businessBrain,
    slug: 'your-business',
  },
  {
    type: 'business_truth.goals',
    get label() { return i18n.global.t('knowledgeNav.item.goals') },
    get description() { return i18n.global.t('knowledgeSections.goalsDescription') },
    icon: PhTarget,
    tile: AREA_TILES.trackingResults,
    slug: 'goals',
  },
  {
    type: 'business_truth.funnel_shape',
    get label() { return i18n.global.t('knowledgeNav.item.howPeopleBuy') },
    get description() { return i18n.global.t('knowledgeSections.howPeopleBuyDescription') },
    icon: PhFunnel,
    tile: AREA_TILES.publishNurture,
    slug: 'how-people-buy',
  },

  // ── Audience ────────────────────────────────────────────────────────
  {
    type: 'business_truth.audience',
    get label() { return i18n.global.t('settings.ai.entity.audience') },
    get description() { return i18n.global.t('knowledgeSections.audienceDescription') },
    icon: PhUsersThree,
    tile: AREA_TILES.businessBrain,
    slug: 'audience',
  },
  {
    type: 'activation.audience_segments',
    get label() { return i18n.global.t('knowledgeNav.item.segments') },
    get description() { return i18n.global.t('knowledgeSections.segmentsDescription') },
    icon: PhUsers,
    tile: AREA_TILES.businessBrain,
    slug: 'segments',
  },
  {
    type: 'activation.objections',
    get label() { return i18n.global.t('knowledgeNav.item.objections') },
    get description() { return i18n.global.t('knowledgeSections.objectionsDescription') },
    icon: PhShieldWarning,
    tile: AREA_TILES.websitesLp,
    slug: 'objections',
  },

  // ── Offer ───────────────────────────────────────────────────────────
  {
    type: 'business_truth.offer',
    get label() { return i18n.global.t('settings.ai.entity.offer') },
    get description() { return i18n.global.t('knowledgeSections.offerDescription') },
    icon: PhStorefront,
    tile: AREA_TILES.commercePayments,
    slug: 'offer',
  },
  {
    type: 'business_truth.positioning',
    get label() { return i18n.global.t('settings.ai.entity.positioning') },
    get description() { return i18n.global.t('knowledgeSections.positioningDescription') },
    icon: PhFlag,
    tile: AREA_TILES.websitesLp,
    slug: 'positioning',
  },
  {
    type: 'activation.value_props',
    get label() { return i18n.global.t('knowledgeNav.item.valuePropositions') },
    get description() { return i18n.global.t('knowledgeSections.valuePropositionsDescription') },
    icon: PhPackage,
    tile: AREA_TILES.commercePayments,
    slug: 'value-propositions',
  },
  {
    type: 'activation.differentiators',
    get label() { return i18n.global.t('knowledgeNav.item.differentiators') },
    get description() { return i18n.global.t('knowledgeSections.differentiatorsDescription') },
    icon: PhLightning,
    tile: AREA_TILES.automations,
    slug: 'differentiators',
  },

  // ── Voice & rules ───────────────────────────────────────────────────
  {
    type: 'marketing_identity.brand_voice',
    get label() { return i18n.global.t('settings.ai.entity.brandVoice') },
    get description() { return i18n.global.t('knowledgeSections.brandVoiceDescription') },
    icon: PhChatCircle,
    tile: AREA_TILES.campaignsContent,
    slug: 'brand-voice',
  },
  {
    type: 'marketing_identity.branding_kit',
    get label() { return i18n.global.t('knowledgeNav.item.brandLook') },
    get description() { return i18n.global.t('knowledgeSections.brandLookDescription') },
    icon: PhPalette,
    tile: AREA_TILES.mediaStudio,
    slug: 'brand-look',
  },
  {
    type: 'customer_evidence.phrases',
    get label() { return i18n.global.t('knowledgeNav.item.customerLanguage') },
    get description() { return i18n.global.t('knowledgeSections.customerLanguageDescription') },
    icon: PhChatCircleText,
    tile: AREA_TILES.campaignsContent,
    slug: 'customer-language',
  },
  {
    type: 'business_truth.constraints',
    get label() { return i18n.global.t('knowledgeNav.item.hardRules') },
    get description() { return i18n.global.t('knowledgeSections.hardRulesDescription') },
    icon: PhProhibit,
    tile: AREA_TILES.automations,
    slug: 'hard-rules',
  },

  // ── Content ─────────────────────────────────────────────────────────
  {
    type: 'activation.content_pillars',
    get label() { return i18n.global.t('knowledgeNav.item.pillars') },
    get description() { return i18n.global.t('knowledgeSections.pillarsDescription') },
    icon: PhSquaresFour,
    tile: AREA_TILES.publishNurture,
    slug: 'pillars',
  },
  {
    type: 'activation.topic_roster',
    get label() { return i18n.global.t('knowledgeNav.item.roster') },
    get description() { return i18n.global.t('knowledgeSections.rosterDescription') },
    icon: PhListChecks,
    tile: AREA_TILES.trackingResults,
    slug: 'roster',
  },
  {
    type: 'activation.lead_magnet_concepts',
    get label() { return i18n.global.t('knowledgeNav.item.teasers') },
    get description() { return i18n.global.t('knowledgeSections.teasersDescription') },
    icon: PhMagnet,
    tile: AREA_TILES.mediaStudio,
    slug: 'lead-magnet-ideas',
  },
]

/** Section definition for an entity type, when it has a row. */
export function findKnowledgeSection(type: string): KnowledgeSectionDef | null {
  return KNOWLEDGE_SECTIONS.find(s => s.type === type) || null
}
